/**
 * 프로젝트 서비스 - 프로젝트 CRUD 및 비즈니스 로직 담당
 * Single Responsibility Principle 적용
 */

import { IProject, IProjectFormData, IProjectStats, ProjectFilterTab } from '../@types';
import { INITIAL_PROJECT_FORM, INITIAL_PROJECT_STATS } from '../@constants';
import { validateProjectForm, ValidationResult } from '../@utils';

declare const wiz: any;

/**
 * 프로젝트 서비스 클래스
 * - 프로젝트 관련 모든 데이터 처리를 담당
 * - 컴포넌트와 데이터 레이어 간의 결합도를 낮춤
 */
export class ProjectService {
    private static instance: ProjectService;

    private constructor() {}

    /** 싱글톤 인스턴스 반환 */
    public static getInstance(): ProjectService {
        if (!ProjectService.instance) {
            ProjectService.instance = new ProjectService();
        }
        return ProjectService.instance;
    }

    /**
     * 프로젝트 목록 조회
     */
    public async getList(filter?: ProjectFilterTab): Promise<{ code: number; data: IProject[] }> {
        try {
            const { code, data } = await wiz.call('list', { filter });
            return { code, data: data || [] };
        } catch (error) {
            console.error('ProjectService.getList error:', error);
            return { code: 500, data: [] };
        }
    }

    /**
     * 프로젝트 상세 조회
     */
    public async getById(id: number): Promise<{ code: number; data: IProject | null }> {
        try {
            const { code, data } = await wiz.call('detail', { id });
            return { code, data };
        } catch (error) {
            console.error('ProjectService.getById error:', error);
            return { code: 500, data: null };
        }
    }

    /**
     * 프로젝트 생성
     */
    public async create(formData: IProjectFormData): Promise<{ code: number; message?: string }> {
        // 유효성 검증
        const validation = this.validate(formData);
        if (!validation.valid) {
            return { code: 400, message: validation.message };
        }

        try {
            const { code, data } = await wiz.call('create', this.prepareFormData(formData));
            return { code, message: code === 200 ? '새 용역이 등록되었습니다.' : data };
        } catch (error) {
            console.error('ProjectService.create error:', error);
            return { code: 500, message: '서버 오류가 발생했습니다.' };
        }
    }

    /**
     * 프로젝트 수정
     */
    public async update(id: number, formData: IProjectFormData): Promise<{ code: number; message?: string }> {
        const validation = this.validate(formData);
        if (!validation.valid) {
            return { code: 400, message: validation.message };
        }

        try {
            const { code, data } = await wiz.call('update', { id, ...this.prepareFormData(formData) });
            return { code, message: code === 200 ? '용역이 수정되었습니다.' : data };
        } catch (error) {
            console.error('ProjectService.update error:', error);
            return { code: 500, message: '서버 오류가 발생했습니다.' };
        }
    }

    /**
     * 프로젝트 삭제
     */
    public async delete(id: number): Promise<{ code: number; message?: string }> {
        try {
            const { code, data } = await wiz.call('delete', { id });
            return { code, message: code === 200 ? '용역이 삭제되었습니다.' : data };
        } catch (error) {
            console.error('ProjectService.delete error:', error);
            return { code: 500, message: '서버 오류가 발생했습니다.' };
        }
    }

    /**
     * 프로젝트 통계 조회
     */
    public async getStats(): Promise<{ code: number; data: IProjectStats }> {
        try {
            const { code, data } = await wiz.call('stats');
            return { code, data: data || INITIAL_PROJECT_STATS };
        } catch (error) {
            console.error('ProjectService.getStats error:', error);
            return { code: 500, data: INITIAL_PROJECT_STATS };
        }
    }

    /**
     * 폼 데이터 유효성 검증
     */
    public validate(formData: IProjectFormData): ValidationResult {
        return validateProjectForm(formData);
    }

    /**
     * 초기 폼 데이터 생성
     */
    public createInitialFormData(): IProjectFormData {
        return { ...INITIAL_PROJECT_FORM };
    }

    /**
     * 폼 데이터 전처리 (API 전송 전)
     */
    private prepareFormData(formData: IProjectFormData): IProjectFormData {
        const prepared = { ...formData };
        
        // 기타(직접입력) 처리
        if (prepared.type === '기타(직접입력)' && prepared.type_etc) {
            prepared.type = prepared.type_etc;
        }
        
        // 참여자 문자열 정리
        if (prepared.participants) {
            prepared.participants = prepared.participants
                .split(',')
                .map(p => p.trim())
                .filter(p => p)
                .join(', ');
        }

        return prepared;
    }
}

/** 싱글톤 인스턴스 export */
export const projectService = ProjectService.getInstance();
